import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { graphql } from 'gatsby';
import { Container } from '@pella/sash-framework';
import { Layout, SEO, Breadcrumbs, Image } from '../../components';
import Cards from '../../components/common/cards';
import CTABanner from '../../components/common/cta-banner';
import Socials from '../../components/page/article/socials';

const Article = (props) => {
	const pageData = props.data.contentstackArticle;
	const [articleDates, setArticleDates] = useState({ created_at: null, updated_at: null });
	const [articleReadTime, setArticleReadTime] = useState(null);

	useEffect(() => {
		generateReadTime();

		if (!pageData.publish_date) return;

		const created = new Date(pageData.publish_date);
		const updated = new Date(pageData.updated_at);
		setArticleDates({
			created_at: created.toLocaleDateString(),
			updated_at: updated.toLocaleDateString(),
		});
	}, [pageData]);

	const generateReadTime = () => {
		const text = document.getElementById('content')?.textContent;
		const words = text.trim().split(/\s+/).length;
		const time = Math.ceil(words / 225);
		setArticleReadTime(time);
	};

	return (
		<Layout>
			{pageData.breadcrumbs && <Breadcrumbs data={pageData.breadcrumbs} />}

			{pageData.modular_blocks?.[0] &&
				pageData.modular_blocks.map((block, index) => {
					if (block.top_content != null) {
						return (
							<div
								key={index}
								className='article-topContent mb-4 pt-6 md:mb-6 md:pt-12'
							>
								<Container className='md:!max-w-[708px] xl:!max-w-[772px]'>
									{block.top_content.h1 && (
										<h1
											className='m-0 font-semibold'
											dangerouslySetInnerHTML={{ __html: block.top_content.h1 }}
										/>
									)}
									{block.top_content.subheading && (
										<p
											className='mt-6 text-lg md:text-xl'
											dangerouslySetInnerHTML={{ __html: block.top_content.subheading }}
										/>
									)}

									<div className='article-metaInfo mt-6 grid auto-rows-auto grid-cols-[1fr_1fr_1fr] items-center px-4 max-md:gap-y-2 md:grid-cols-[minmax(min-content,max-content)_1fr_minmax(min-content,max-content)]'>
										<div className='article-author col-start-1 flex flex-row items-center'>
											{pageData.file?.url && (
												<div className='mr-2 flex h-6 w-6 items-center justify-center rounded-full border border-secondary-dark p-1'>
													<Image data={pageData} />
												</div>
											)}

											{pageData.author && <p className='m-0 flex-12/12 text-xs font-semibold sm:flex-none'>By: {pageData.author}</p>}
										</div>

										<div className='article-metaDates col-span-3 flex flex-row flex-wrap text-gray-60 max-md:row-start-2 md:col-span-1'>
											{articleDates.created_at && (
												<p
													data-testid='article-created'
													className='my-0 flex-none text-[10px] md:before:px-2 md:before:text-gray-15 md:before:content-["|"]'
												>
													Published {articleDates.created_at}
												</p>
											)}
											{articleDates.updated_at && (
												<p
													data-testid='article-updated'
													className='my-0 flex-none text-[10px] max-md:ml-3 md:before:px-2 md:before:text-gray-15 md:before:content-["|"]'
												>
													Updated {articleDates.updated_at}
												</p>
											)}
											{articleReadTime && (
												<p
													data-testid='article-length'
													className='my-0 flex-none text-[10px] font-semibold max-md:ml-auto md:before:px-2 md:before:text-gray-15 md:before:content-["|"]'
												>
													{articleReadTime} min read
												</p>
											)}
										</div>

										<Socials
											url={pageData.url}
											className='article-share col-span-2 justify-end text-primary md:col-span-1'
										/>
									</div>
								</Container>
							</div>
						);
					}

					if (block.image != null && block.image.image?.[0]) {
						return (
							<div
								key={index}
								className='article-images my-4 md:my-6'
							>
								<Container className='max-md:px-0 md:!max-w-[708px] xl:!max-w-[772px]'>
									{block.image.image.map((image, index) => (
										<Image
											data={image}
											className='mx-auto !block'
											key={index}
										/>
									))}
								</Container>
							</div>
						);
					}

					if (block.content != null) {
						return (
							<div
								id={block.content.anchor}
								key={index}
								className='article-content my-4 md:my-6'
							>
								<Container className='md:!max-w-[708px] xl:!max-w-[772px]'>
									{block.content.h2 && (
										<h2
											className='mb-6 md:mb-1'
											dangerouslySetInnerHTML={{ __html: block.content.h2 }}
										/>
									)}
									{block.content.h3 && (
										<h3
											className='mb-6 md:mb-1'
											dangerouslySetInnerHTML={{ __html: block.content.h3 }}
										/>
									)}
									{block.content.description && (
										<div
											className='text-lg md:text-xl'
											dangerouslySetInnerHTML={{ __html: block.content.description }}
										/>
									)}
								</Container>
							</div>
						);
					}

					if (block.ctas != null) {
						return (
							<div
								key={index}
								className='article-ctas mt-4 md:mt-12'
							>
								<Container className='max-md:px-0'>
									<div className='grid grid-cols-1 gap-y-3 xl:grid-cols-[1.125fr_2fr] xl:gap-x-6'>
										{block.ctas.cta_banner.map((banner, index) => (
											<CTABanner
												data={banner}
												key={index}
											/>
										))}
									</div>
								</Container>
							</div>
						);
					}

					if (block.cards?.cards?.[0]) {
						return (
							<Cards
								key={index}
								data={block.cards}
								className='py-8 md:py-6 xl:py-12'
							/>
						);
					}
				})}
		</Layout>
	);
};

Article.propTypes = {
	data: PropTypes.object,
};

export default Article;

// eslint-disable-next-line react/prop-types -- gatsby component
export const Head = ({ data }) => <SEO {...data.contentstackArticle.seo} />;

export const query = graphql`
	query ($uid: String!) {
		contentstackArticle(uid: { eq: $uid }) {
			url
			breadcrumbs {
				...Breadcrumbs
			}
			author
			file {
				url
				gatsbyImageData(quality: 90, width: 24)
			}
			publish_date
			updated_at
			modular_blocks {
				top_content {
					h1
					subheading
				}
				image {
					image {
						...Image
					}
				}
				content {
					anchor
					h2
					h3
					description
				}
				ctas {
					cta_banner {
						...CTABanner
					}
				}
				cards {
					...CardSection
				}
			}
			seo {
				...SEO
			}
		}
	}
`;
